import { createSignal } from 'solid-js';
import { createMagicDoorContext } from '~/contexts/utils';
import { RentPaymentSettingsRepository } from '~/repositories/settings/rentPaymentSettingsRepository';
import type { Accessor } from 'solid-js';
import type { RentPaymentSettings, RentPaymentSettingsCompanyPortalTypedSettingsWithOverridesDocument } from '~/swagger/settings/Api';

interface RentPaymentSettingsContextValue {
  addRentPaymentSettings: (payload: RentPaymentSettings) => Promise<void>;
  addRentPaymentSettingsByLevel: (level: string, levelId: string, payload: RentPaymentSettings) => Promise<void>;
  getRentPaymentSettings: () => Promise<RentPaymentSettings | undefined>;
  getRentPaymentSettingsByLevel: (level: string, levelId: string) => Promise<RentPaymentSettings | undefined>;
  getAllRentPaymentSettings: () => Promise<RentPaymentSettingsCompanyPortalTypedSettingsWithOverridesDocument | undefined>;
  isLoading: Accessor<boolean>;
  error: Accessor<Error | undefined>;
  rentPaymentSettingsWithNoOverrides: Accessor<RentPaymentSettings | undefined>;
  allRentPaymentSettings: Accessor<RentPaymentSettingsCompanyPortalTypedSettingsWithOverridesDocument | undefined>;
  deleteRentPaymentSettingsByLevel: (level: string, levelId: string) => Promise<void>;
}

export const [RentPaymentSettingsProvider, useRentPaymentSettings] = createMagicDoorContext<RentPaymentSettingsContextValue>(
  'RentPaymentSettings',
  () => {
    const repo = new RentPaymentSettingsRepository();
    const [rentPaymentSettingsWithNoOverrides, setRentPaymentSettingsWithNoOverrides] = createSignal<RentPaymentSettings | undefined>(
      undefined
    );
    const [allRentPaymentSettings, setAllRentPaymentSettings] = createSignal<
      RentPaymentSettingsCompanyPortalTypedSettingsWithOverridesDocument | undefined
    >(undefined);
    const [isLoading, setIsLoading] = createSignal<boolean>(false);
    const [error, setError] = createSignal<Error | undefined>(undefined);

    const addRentPaymentSettings = async (payload: RentPaymentSettings) => {
      setIsLoading(true);
      try {
        await repo.addRentPaymentSettings(payload);
      } catch (err) {
        setError(err instanceof Error ? err : new Error(String(err)));
      } finally {
        setIsLoading(false);
      }
    };

    const addRentPaymentSettingsByLevel = async (level: string, levelId: string, payload: RentPaymentSettings) => {
      setIsLoading(true);
      try {
        await repo.addRentPaymentSettingsByLevel(level, levelId, payload);
      } catch (err) {
        setError(err instanceof Error ? err : new Error(String(err)));
      } finally {
        setIsLoading(false);
      }
    };

    const getRentPaymentSettings = async (): Promise<RentPaymentSettings | undefined> => {
      setIsLoading(true);
      try {
        const result = await repo.getRentPaymentSettings();
        setRentPaymentSettingsWithNoOverrides(result);
        return result;
      } catch (err) {
        setError(err instanceof Error ? err : new Error(String(err)));
      } finally {
        setIsLoading(false);
      }
    };

    const getRentPaymentSettingsByLevel = async (level: string, levelId: string): Promise<RentPaymentSettings | undefined> => {
      setIsLoading(true);
      try {
        const result = await repo.getRentPaymentSettingsByLevel(level, levelId);
        setRentPaymentSettingsWithNoOverrides(result);
        return result;
      } catch (err) {
        setError(err instanceof Error ? err : new Error(String(err)));
      } finally {
        setIsLoading(false);
      }
    };

    const getAllRentPaymentSettings = async (): Promise<RentPaymentSettingsCompanyPortalTypedSettingsWithOverridesDocument | undefined> => {
      setIsLoading(true);
      try {
        const result = await repo.getAllRentPaymentSettings();
        setAllRentPaymentSettings(result);
        return result;
      } catch (err) {
        setError(err instanceof Error ? err : new Error(String(err)));
      } finally {
        setIsLoading(false);
      }
    };

    const deleteRentPaymentSettingsByLevel = async (level: string, levelId: string) => {
      setIsLoading(true);
      try {
        await repo.deleteRentPaymentSettingsByLevel(level, levelId);
      } catch (err) {
        setError(err instanceof Error ? err : new Error(String(err)));
      } finally {
        setIsLoading(false);
      }
    };

    return {
      deleteRentPaymentSettingsByLevel,
      addRentPaymentSettings,
      addRentPaymentSettingsByLevel,
      getRentPaymentSettings,
      getRentPaymentSettingsByLevel,
      getAllRentPaymentSettings,
      allRentPaymentSettings,
      isLoading,
      rentPaymentSettingsWithNoOverrides,
      error,
    };
  }
);
